/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Box, Text } from "@nordcloud/gnui";
import { InlineDropdownAREnvTypes } from "~/components/AssignmentRules/InlineDropdownAREnvTypes";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { showSuccess } from "~/components/Toast";
import { DetailRow } from "~/components/Utils/DetailRow";
import useInterval from "~/hooks/useInterval";
import { EvaluationStatus } from "~/models";
import {
  AssignmentRule,
  AssignmentRuleFlat,
  AssignmentRuleRouteParams,
} from "~/models/assignmentRule";
import {
  displayAREvaluationStatus,
  generateActionSuccessText,
  useAxios,
} from "~/tools";
import { InlineDropdownAREnvStatus } from "./InlineDropdownAREnvStatus";
import { InlineServiceLevelAR } from "./InlineServiceLevelAR";
import { ARValues } from "./types";

type AssignmentRuleDetailsInfoProps = {
  assignmentRule: AssignmentRule;
  refetch: () => Promise<void>;
  canEdit?: boolean;
};

function AssignmentRuleDetailsInfo({
  assignmentRule,
  refetch,
  canEdit,
}: AssignmentRuleDetailsInfoProps) {
  const {
    customerId,
    assignmentRuleId,
  } = useParams<AssignmentRuleRouteParams>();

  const checkStatus = React.useCallback(
    (status: string) => {
      if (
        status === EvaluationStatus.SUCCEEDED ||
        status === EvaluationStatus.FAILED
      ) {
        return;
      }

      refetch();
    },
    [refetch]
  );

  useInterval(() => {
    checkStatus(assignmentRule.evaluationStatus);
  }, 5000);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const assignmentRuleFlat = {
    ...assignmentRule,
    attachedServiceLevel: assignmentRule?.attachedServiceLevel
      ? {
          capacityBasedFeeType:
            assignmentRule?.attachedServiceLevel?.capacityBasedFeeType,
          capacityBasedPercentageFee:
            assignmentRule?.attachedServiceLevel?.capacityBasedPercentageFee,
          serviceLevelId:
            assignmentRule?.attachedServiceLevel?.serviceLevel?.id,
        }
      : null,
  };

  const onSubmit = async (values: AssignmentRuleFlat | ARValues) => {
    try {
      await Axios().put(
        `/customers/${customerId}/assignmentRules/${assignmentRuleId}`,
        values
      );
      showSuccess(
        generateActionSuccessText(`Assignment Rule ${assignmentRule.name}`)()(
          "updated"
        )()
      );
      await refetch();
    } catch (e) {
      showError(e?.response, 6000);
    }
  };

  return (
    <Box spacing="spacing04">
      <Row>
        <Col xs={8} sm={8} md={6} lg={6}>
          <Text weight="medium">Assignment Rule Details</Text>

          <DetailRow title="Name">
            <InlineEditText
              name="name"
              defaultValue={assignmentRuleFlat.name}
              onSubmit={(name) => onSubmit({ ...assignmentRuleFlat, name })}
              canEdit={canEdit}
            />
          </DetailRow>

          <DetailRow title="Processing Status">
            {displayAREvaluationStatus(assignmentRule.evaluationStatus)}
          </DetailRow>

          <DetailRow title="Environment Types">
            <InlineDropdownAREnvTypes
              item={assignmentRuleFlat}
              onSubmit={onSubmit}
              canEdit={canEdit}
            />
          </DetailRow>

          <DetailRow title="Environment Status">
            <InlineDropdownAREnvStatus
              item={assignmentRuleFlat}
              onSubmit={onSubmit}
              refetch={refetch}
              canEdit={canEdit}
            />
          </DetailRow>

          <DetailRow title="Description">
            <InlineEditText
              name="description"
              defaultValue={assignmentRuleFlat.description}
              textArea
              onSubmit={(description) =>
                onSubmit({ ...assignmentRuleFlat, description })
              }
              canEdit={canEdit}
            />
          </DetailRow>

          <DetailRow title="Service level">
            <InlineServiceLevelAR
              assignmentRule={assignmentRule}
              refetch={refetch}
              canEdit={canEdit}
            />
          </DetailRow>
        </Col>
      </Row>
    </Box>
  );
}

export { AssignmentRuleDetailsInfo };
